<script setup>
import { computed } from 'vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import WinRateCard from '../../analytics/WinRateCard.vue';
import SalesVelocityCard from '../../analytics/SalesVelocityCard.vue';
import PipelineSummaryCard from '../../analytics/PipelineSummaryCard.vue';
import LeadDistributionCard from '../../analytics/LeadDistributionCard.vue';
import PipelineAnalysisCard from '../../analytics/PipelineAnalysisCard.vue';

const props = defineProps({
  dashboardData: {
    type: Object,
    default: null,
  },
  hasNoData: {
    type: Boolean,
    default: false,
  },
  dateRange: {
    type: Object,
    default: () => ({
      startDate: null,
      endDate: null,
    }),
  },
  selectedPipelineId: {
    type: [String, Number],
    default: null,
  },
});

const startDate = computed(() => {
  return props.dateRange.startDate
    ? new Date(props.dateRange.startDate)
    : new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
});

const endDate = computed(() => {
  return props.dateRange.endDate
    ? new Date(props.dateRange.endDate)
    : new Date();
});

// Derive first real pipeline ID from analytics data for PipelineAnalysisCard
const effectivePipelineId = computed(() => {
  if (props.selectedPipelineId) return props.selectedPipelineId;
  const byPipeline =
    props.dashboardData?.sales_velocity?.by_pipeline ||
    props.dashboardData?.win_rate?.by_pipeline ||
    [];
  return byPipeline.length > 0 ? byPipeline[0].pipeline_id : null;
});
</script>

<template>
  <div class="space-y-6 max-w-[1400px] mx-auto">
    <!-- Empty State -->
    <div
      v-if="hasNoData"
      class="flex flex-col items-center justify-center gap-4 py-12"
    >
      <Icon icon="i-lucide-box" class="size-16 text-n-slate-9" />
      <div class="text-center">
        <p class="text-base font-medium text-n-slate-12 mb-2">
          {{ $t('PIPELINE.ANALYTICS.EMPTY_STATE.TITLE') }}
        </p>
        <p class="text-sm text-n-slate-11 max-w-md">
          {{ $t('PIPELINE.ANALYTICS.EMPTY_STATE.DESCRIPTION') }}
        </p>
      </div>
    </div>

    <!-- Dashboard Grid -->
    <template v-else-if="dashboardData">
      <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
        <!-- Win Rate Card -->
        <WinRateCard :data="dashboardData.win_rate" />

        <!-- Sales Velocity Card -->
        <SalesVelocityCard :data="dashboardData.sales_velocity" />

        <!-- Pipeline Summary Card -->
        <PipelineSummaryCard :data="dashboardData.pipeline_summary" />

        <!-- Lead Distribution Card -->
        <LeadDistributionCard :data="dashboardData.lead_distribution" />
      </div>

      <!-- Pipeline Analysis Section - shown when a pipeline ID is available -->
      <PipelineAnalysisCard
        v-if="effectivePipelineId"
        :start-date="startDate"
        :end-date="endDate"
        :pipeline-id="effectivePipelineId"
      />
    </template>

    <!-- No Data State -->
    <div v-else class="flex flex-col items-center justify-center gap-3 py-12">
      <Icon icon="i-lucide-bar-chart-3" class="size-12 text-n-slate-9" />
      <p class="text-sm text-n-slate-11">
        {{ $t('PIPELINE.ANALYTICS.NO_DATA') }}
      </p>
    </div>
  </div>
</template>
